import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";
import { api, fmtMXN, fmtDate, todayISO } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

export default function InsurancePage() {
    const { isAdmin, canWrite } = useAuth();
    const [items, setItems] = useState([]);
    const [cars, setCars] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const empty = {
        car_id: "", provider: "", policy_number: "", coverage_type: "Amplia",
        start_date: todayISO(), end_date: todayISO(), cost: "", notes: "",
    };
    const [form, setForm] = useState(empty);

    const load = async () => {
        const [a, b] = await Promise.all([api.get("/insurance"), api.get("/cars")]);
        setItems(a.data); setCars(b.data);
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm({ ...empty, car_id: cars[0]?.id || "" }); setOpen(true); };
    const openEdit = (i) => {
        setEditing(i);
        setForm({
            car_id: i.car_id, provider: i.provider, policy_number: i.policy_number || "",
            coverage_type: i.coverage_type || "Amplia", start_date: i.start_date, end_date: i.end_date,
            cost: i.cost || "", notes: i.notes || "",
        });
        setOpen(true);
    };
    const submit = async () => {
        if (!form.car_id || !form.provider) return toast.error("Auto y aseguradora son obligatorios");
        const payload = { ...form, cost: form.cost ? Number(form.cost) : null };
        try {
            if (editing) await api.put(`/insurance/${editing.id}`, payload);
            else await api.post("/insurance", payload);
            toast.success("Guardado");
            setOpen(false); load();
        } catch { toast.error("Error"); }
    };
    const remove = async (i) => {
        if (!window.confirm("¿Eliminar póliza?")) return;
        await api.delete(`/insurance/${i.id}`);
        toast.success("Eliminada"); load();
    };

    const today = new Date();
    const status = (end) => {
        const d = new Date(end);
        const days = Math.ceil((d - today) / (1000 * 60 * 60 * 24));
        if (days < 0) return { label: "Vencida", cls: "bg-rose-100 text-rose-700" };
        if (days <= 30) return { label: `Vence en ${days} d`, cls: "bg-amber-100 text-amber-700" };
        return { label: `Vigente · ${days} d`, cls: "bg-emerald-100 text-emerald-700" };
    };

    return (
        <div className="space-y-6" data-testid="insurance-page">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Administrativo</p>
                    <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1 font-display">Seguros</h1>
                    <p className="text-sm text-slate-500 mt-1">Pólizas, coberturas y vencimientos por auto.</p>
                </div>
                {canWrite && (
                    <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-insurance-button">
                        <Plus size={16} className="mr-1" /> Nueva póliza
                    </Button>
                )}
            </header>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {items.length === 0 && (
                    <div className="col-span-full text-center text-slate-400 py-16">Aún no hay pólizas registradas.</div>
                )}
                {items.map((i) => {
                    const car = cars.find((c) => c.id === i.car_id);
                    const s = status(i.end_date);
                    return (
                        <Card key={i.id} className="border-slate-200 shadow-sm" data-testid={`insurance-${i.id}`}>
                            <CardHeader className="flex flex-row items-start justify-between">
                                <div>
                                    <CardTitle className="text-lg font-display font-medium flex items-center gap-2">
                                        <ShieldCheck size={18} className="text-slate-500" />
                                        {i.provider}
                                    </CardTitle>
                                    <div className="text-xs text-slate-500 mt-1">{car?.plate} · {car?.brand} {car?.model}</div>
                                </div>
                                <Badge className={`${s.cls} hover:${s.cls}`}>{s.label}</Badge>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-3 text-sm">
                                    <Field label="Póliza" value={i.policy_number || "—"} />
                                    <Field label="Cobertura" value={i.coverage_type || "—"} />
                                    <Field label="Vigencia" value={`${fmtDate(i.start_date)} → ${fmtDate(i.end_date)}`} />
                                    <Field label="Costo" value={fmtMXN(i.cost || 0)} />
                                </div>
                                {i.notes && <p className="text-xs text-slate-500 mt-3">{i.notes}</p>}
                                <div className="mt-4 flex justify-end gap-1">
                                    {canWrite && (
                                        <button className="p-1.5 rounded hover:bg-slate-100" onClick={() => openEdit(i)} data-testid={`edit-ins-${i.id}`}><Pencil size={14} /></button>
                                    )}
                                    {isAdmin && (
                                        <button className="p-1.5 rounded hover:bg-rose-50 text-rose-600" onClick={() => remove(i)} data-testid={`del-ins-${i.id}`}><Trash2 size={14} /></button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-display">{editing ? "Editar póliza" : "Nueva póliza"}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                            <Label className="text-xs">Auto</Label>
                            <Select value={form.car_id} onValueChange={(v) => setForm({ ...form, car_id: v })}>
                                <SelectTrigger data-testid="ins-car"><SelectValue placeholder="Selecciona auto" /></SelectTrigger>
                                <SelectContent>{cars.map((c) => <SelectItem key={c.id} value={c.id}>{c.plate} · {c.brand}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Aseguradora</Label>
                            <Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} data-testid="ins-provider" />
                        </div>
                        <div>
                            <Label className="text-xs">No. Póliza</Label>
                            <Input value={form.policy_number} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Cobertura</Label>
                            <Input value={form.coverage_type} onChange={(e) => setForm({ ...form, coverage_type: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Costo (MXN)</Label>
                            <Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Inicio</Label>
                            <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs">Fin</Label>
                            <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="ins-end" />
                        </div>
                        <div className="col-span-2">
                            <Label className="text-xs">Notas</Label>
                            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button className="bg-slate-900 hover:bg-slate-800 text-white" onClick={submit} data-testid="save-insurance">Guardar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function Field({ label, value }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
            <div className="text-sm text-slate-900 mt-0.5">{value}</div>
        </div>
    );
}
